// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useState } from 'react';

import type { ManualFeaturePayload } from './holeGeometryDraft';

export type ManualHoleFeaturesState = {
  /**
   * Writes a hole's marked positions. Resolves false on any failure, having reported it
   * through {@link error} — the caller keeps the draft so nothing a person clicked is lost.
   */
  save: (features: ManualFeaturePayload[]) => Promise<boolean>;
  saving: boolean;
  /** Set when the last save failed, cleared when the next one starts. */
  error: boolean;
};

/**
 * Saving hand-placed tee and green positions.
 *
 * Writes only. Reading them back is {@link useHoleFeatures}, which the editor already uses
 * and refreshes after a save, so there is exactly one description of what the server holds
 * rather than a local copy that can drift from it.
 */
export function useManualHoleFeatures(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): ManualHoleFeaturesState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const save = useCallback(
    async (features: ManualFeaturePayload[]): Promise<boolean> => {
      if (!token || !courseId || features.length === 0) return false;

      setSaving(true);
      setError(false);
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/features/manual`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ features }),
          }
        );
        if (!response.ok) {
          setError(true);
          return false;
        }
        return true;
      } catch {
        setError(true);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [apiBaseUrl, courseId, token]
  );

  return { save, saving, error };
}
